import { makeStore, nextTick, noop } from '@/utils'
import { assoc, compose, mergeLeft } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'

type SkipData = {
  text: string
  skipButton: string
  withModal: boolean
  onSkipClick: () => void
}

type State = {
  skipData: SkipData
  buttonText: string
  nextDisabled: boolean
  nextLoading: boolean
  onNextClick: () => Promise<void>
  onBackClick?: () => Promise<void>
}

const ACTION_TYPES = {
  NEXT_DISABLED: 'NEXT_DISABLED',
  NEXT_LOADING: 'NEXT_LOADING',
  SET_ON_NEXT_CLICK: 'SET_ON_NEXT_CLICK',
  SET_ON_BACK_CLICK: 'SET_ON_BACK_CLICK',
  SET_SKIP_DATA: 'SET_SKIP_DATA',
  SET_BUTTON_TEXT: 'SET_BUTTON_TEXT',
} as const

type Action =
  | { type: typeof ACTION_TYPES.NEXT_DISABLED; payload: boolean }
  | { type: typeof ACTION_TYPES.NEXT_LOADING; payload: boolean }
  | {
      type: typeof ACTION_TYPES.SET_ON_NEXT_CLICK
      payload: () => Promise<void>
    }
  | {
      type: typeof ACTION_TYPES.SET_ON_BACK_CLICK
      payload: (() => Promise<void>) | undefined
    }
  | { type: typeof ACTION_TYPES.SET_SKIP_DATA; payload: Partial<SkipData> }
  | { type: typeof ACTION_TYPES.SET_BUTTON_TEXT; payload: string }

const setNextDisabled = (value: boolean): Action => ({
  type: ACTION_TYPES.NEXT_DISABLED,
  payload: value,
})

const setNextLoading = (value: boolean): Action => ({
  type: ACTION_TYPES.NEXT_LOADING,
  payload: value,
})

const setOnNextClick = (callback: () => Promise<void>): Action => ({
  type: ACTION_TYPES.SET_ON_NEXT_CLICK,
  payload: callback,
})

const setOnBackClick = (
  callback: (() => Promise<void>) | undefined,
): Action => ({
  type: ACTION_TYPES.SET_ON_BACK_CLICK,
  payload: callback,
})

const setSkipData = (value: Partial<SkipData>): Action => ({
  type: ACTION_TYPES.SET_SKIP_DATA,
  payload: value,
})

const setButtonText = (value: string): Action => ({
  type: ACTION_TYPES.SET_BUTTON_TEXT,
  payload: value,
})

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTION_TYPES.NEXT_DISABLED:
      return assoc('nextDisabled', action.payload, state)
    case ACTION_TYPES.NEXT_LOADING:
      return assoc('nextLoading', action.payload, state)
    case ACTION_TYPES.SET_ON_NEXT_CLICK:
      return assoc('onNextClick', action.payload, state)
    case ACTION_TYPES.SET_ON_BACK_CLICK:
      return assoc('onBackClick', action.payload, state)
    case ACTION_TYPES.SET_BUTTON_TEXT:
      return assoc('buttonText', action.payload, state)
    case ACTION_TYPES.SET_SKIP_DATA:
      return {
        ...state,
        skipData: mergeLeft(action.payload, state.skipData),
      }
    default:
      return state
  }
}

const initialState: State = {
  skipData: {
    text: '',
    skipButton: '',
    withModal: true,
    onSkipClick: noop,
  },
  buttonText: '',
  nextDisabled: false,
  nextLoading: false,
  onNextClick: async () => {},
  onBackClick: undefined,
}

export const [StepperProvider, useDispatchStepper, useStepperStore] = makeStore<
  State,
  Action
>(initialState, reducer)

export const useOnNextClick = (onNextClick: () => Promise<void>) => {
  const callback = useCallback(async () => {
    const pass = await onNextClick()
    await nextTick() // also waits for form updates to finish after clicking next
    return pass
  }, [onNextClick])

  const dispatch = useDispatchStepper()
  const dispatchSetOnNextClick = compose(dispatch, setOnNextClick)

  useEffect(() => {
    dispatchSetOnNextClick(callback)
    return () => {
      dispatchSetOnNextClick(initialState.onNextClick)
    }
  }, [callback])
}

export const useOnBackClick = (onBackClick: () => Promise<void>) => {
  const callback = useCallback(async () => {
    const pass = await onBackClick()
    await nextTick() // also waits for form updates to finish after clicking back button
    return pass
  }, [onBackClick])

  const dispatch = useDispatchStepper()
  const dispatchSetOnBackClick = compose(dispatch, setOnBackClick)

  useEffect(() => {
    dispatchSetOnBackClick(callback)
    return () => {
      dispatchSetOnBackClick(initialState.onBackClick)
    }
  }, [callback])
}

export const useNextDisabled = (
  factory: () => boolean,
  deps: unknown[] = [],
) => {
  const nextDisabled = useMemo(() => factory(), deps)
  const dispatch = useDispatchStepper()
  const dispatchSetNextDisabled = compose(dispatch, setNextDisabled)

  useEffect(() => {
    dispatchSetNextDisabled(nextDisabled)
    return () => {
      dispatchSetNextDisabled(initialState.nextDisabled)
    }
  }, [nextDisabled])
}

export const useNextLoading = (
  factory: () => boolean,
  deps: unknown[] = [],
) => {
  const nextLoading = useMemo(() => factory(), deps)
  const dispatch = useDispatchStepper()
  const dispatchSetNextLoading = compose(dispatch, setNextLoading)

  useEffect(() => {
    dispatchSetNextLoading(nextLoading)
    return () => {
      dispatchSetNextLoading(initialState.nextLoading)
    }
  }, [nextLoading])
}

export const useSetButtonText = (
  factory: () => string,
  deps: unknown[] = [],
) => {
  const buttonText = useMemo(() => factory(), deps)
  const dispatch = useDispatchStepper()
  const dispatchSetButtonText = compose(dispatch, setButtonText)

  useEffect(() => {
    dispatchSetButtonText(buttonText)
    return () => {
      dispatchSetButtonText(initialState.buttonText)
    }
  }, [buttonText])
}

export const useSkipData = (
  factory: () => Partial<SkipData>,
  deps: unknown[] = [],
) => {
  const skipData = useMemo(() => factory(), deps)
  const dispatch = useDispatchStepper()
  const dispatchSetSkipData = compose(dispatch, setSkipData)

  useEffect(() => {
    dispatchSetSkipData(skipData)
    return () => {
      dispatchSetSkipData(initialState.skipData)
    }
  }, [skipData])
}
